import type { SetStateAction } from 'react';

import type { QueryClient } from '@tanstack/react-query';
import type { LOAN_REQUEST_OFFER_SOURCE_WITHOUT_MANUAL, LOAN_REQUEST_OFFER_STATE } from 'cpool-constants';
import { LOAN_REQUEST_OFFER_STATES } from 'cpool-constants';
import type {
    SchemaLoanRequestResolver_Get,
    SchemaLoanRequestResolver_GetOfferRequestState,
} from 'cpool-tables/backend-resolvers';
import { schemaLoanRequestResolver_GetOfferRequestState } from 'cpool-tables/backend-resolvers';
import { sleep } from 'cpool-utils';
import type { TFunction } from 'i18next';
import { toast } from 'react-toastify';

import type { SourceTypeObject } from '../RequestConfiguration/types';
import { API_EXTERNAL } from '~/config.ts';
import { fetcher } from '~/queryUtils/useQuery';
import { queryKeys } from '~/utils/queryKeys.ts';

type Props = {
    offerRequestId: string;
    queryClient: QueryClient;
    lead: SchemaLoanRequestResolver_Get;
    setIsOfferRequestActive: (value: SetStateAction<SourceTypeObject>) => void;
    updateRequestId: (requestId: string) => void;
    t: TFunction<'de' | 'en', undefined>;
    waitTime?: number;
    bank: LOAN_REQUEST_OFFER_SOURCE_WITHOUT_MANUAL;
    retries?: number;
};

// NOTE: we still have to use polling for the following reasons:
// 1. The saga projection does not wait for the request to Europace to be completed,
//     therefore finishes before any new event in the chain is published.
// 2. The chain is finished as soon as no events are still in progress that are part of the event chain.
// 3. The chain is interrupted until the request to Europace is finished.
export const verifyBankOffersReceived = async ({
    bank,
    lead,
    offerRequestId,
    queryClient,
    retries = API_EXTERNAL.EUROPACE.REQUEST_OFFERS.MAX_RETRIES,
    setIsOfferRequestActive,
    t,
    updateRequestId,
    waitTime = API_EXTERNAL.EUROPACE.REQUEST_OFFERS.RETRY_INTERVAL,
}: Props) => {
    try {
        let requestOffersState: LOAN_REQUEST_OFFER_STATE | null = LOAN_REQUEST_OFFER_STATES.PENDING;
        let failedReason: string[] | null | undefined = null;
        let counter = 0;
        /* eslint-disable no-await-in-loop */
        while (requestOffersState === LOAN_REQUEST_OFFER_STATES.PENDING && counter < retries) {
            await sleep(waitTime);
            const response: SchemaLoanRequestResolver_GetOfferRequestState = await fetcher({
                meta: {
                    queryUrl: `/query/loan-request/getOfferRequestState?id=${lead.id}&requestId=${offerRequestId}&source=${bank}`,
                    schema: schemaLoanRequestResolver_GetOfferRequestState,
                },
            });
            requestOffersState = response.state;
            failedReason = response.failedReason;
            counter += 1;
        }
        /* eslint-enable no-await-in-loop */
        if (requestOffersState === LOAN_REQUEST_OFFER_STATES.PENDING) {
            toast.error(
                t('BANK_CREDIT_REQUEST.OFFER_REQUEST_TIMEOUT', {
                    bank: bank[0].toUpperCase() + bank.slice(1).toLowerCase(),
                }),
            );
            setIsOfferRequestActive((prev) => ({ ...prev, [bank]: false }));
            return;
        }
        if (requestOffersState === LOAN_REQUEST_OFFER_STATES.FAILED || requestOffersState === null) {
            toast.error(
                <div className="flex flex-col gap-2">
                    <p>
                        {t('BANK_CREDIT_REQUEST.OFFER_REQUEST_FAILED', {
                            bank: bank[0].toUpperCase() + bank.slice(1).toLowerCase(),
                        })}
                    </p>
                    {failedReason && (
                        <>
                            <p>{t('BANK_CREDIT_REQUEST.ERRORS_LIST_HEADER')}</p>
                            <ul className="list-disc pl-5">
                                {failedReason.map((reason) => (
                                    // @ts-expect-error -- TS doesn't know that the enum values are litteras
                                    <li key={reason}>{t(`BANK_CREDIT_REQUEST.FAILED_REASONS.${reason}`)}</li>
                                ))}
                            </ul>
                        </>
                    )}
                </div>,
            );
            setIsOfferRequestActive((prev) => ({ ...prev, [bank]: false }));
            return;
        }
    } catch (error) {
        console.error(error);
        toast.error(
            t('BANK_CREDIT_REQUEST.OFFER_REQUEST_FAILED', {
                bank: bank[0].toUpperCase() + bank.slice(1).toLowerCase(),
            }),
        );
        setIsOfferRequestActive((prev) => ({ ...prev, [bank]: false }));
        return;
    }

    updateRequestId(offerRequestId);

    queryClient.invalidateQueries({ queryKey: queryKeys.getOffersByRequestId(lead.id, offerRequestId) });
    queryClient.invalidateQueries({ queryKey: queryKeys.getBankOffersByLeadId(lead.id) });
    queryClient.invalidateQueries({
        queryKey: queryKeys.getOfferRequestConditionsLatest(lead.id),
    });

    setIsOfferRequestActive((prev) => ({ ...prev, [bank]: false }));
};
